// Buying is priced per unit and clamped three ways: the shop's stock, the player's purse, and the
// step the button was built with. `countFor` is the only place that clamp lives, so the label,
// the enabled gate and the purchase itself cannot disagree about how much a click hands over.
//
// Everything here reads the market at call time. Buy buttons are constructed once and frozen into
// an event object (`events/city/index.js`, `events/road/index.js` build the whole modal up front),
// so a value captured in the factory would still be reporting the shop's opening stock ten
// purchases later. `party` and `political` are long-lived mutable instances, so reading through
// the context on every render is both correct and cheap.

const roundCoins = n => Math.round(n * 10) / 10;

const marketFor = ({ party, calendar, political }) => political.market(party, calendar);

const stateFor = (context, type) => {
  const { prices, quantities } = marketFor(context).getMarketState();
  return { price: prices[type], stock: quantities[type] };
};

// How many of `type` this button would actually hand over right now. Takes an already-read
// `{ price, stock }` so a caller that also needs the price does not read the market twice. In a
// town `political.market()` hands back the city's persistent instance, but on the road it
// constructs a fresh Market on every call, so the reads are worth counting.
const countFor = ({ price, stock }, purse, step) => {
  if (!price) return 0;
  return Math.max(0, Math.min(step, stock, Math.floor(purse / price)));
};

const affordable = (context, type, step) =>
  countFor(stateFor(context, type), context.party.resources.attribs.coins.value, step);

const attempt = (gain, cost) => {
  const result = {
    success: true,
    gain: { ...gain },
    cost: { coins: -cost }
  };

  return result;
};

const executeSideEffects = (context, result) => {
  const { resources } = context.party;
  const { political } = context;
  const { gain, cost } = result;

  resources.delta({ ...gain, ...cost }, context);
  political.market(context.party, context.calendar).delta({ ...gain, ...cost }, context);
};

// The shape every buy button shares. `step` is the most a single click will buy; a bulk button
// wears the live number in its title, so a purse that only covers seven reads `Buy Food x7` and
// the player is told the cap without being taught it. Singles keep a bare label — `x1` on every
// button would be noise.
const buyAction =
  ({ type, resource, label, hotkey, step = 1, gainFor, messageText, cooldown = 0.2 }) =>
  (context, notifyDone) => {
    // One market read, then everything the caller needs off it.
    const read = ctx => {
      const state = stateFor(ctx, resource);
      const count = countFor(state, ctx.party.resources.attribs.coins.value, step);
      return { ...state, count };
    };

    return {
      // Counts down as the purse empties — x10, x7, x3. At nothing affordable it falls back to
      // advertising the lot rather than reading `x0`, which told the player only that the button
      // was dead, which the greying already said.
      title: ctx => (step === 1 ? label : `${label} x${read(ctx).count || step}`),
      hotkey,
      type,
      enabled: ctx => read(ctx).count >= 1,
      execute: ctx => {
        const { price, count } = read(ctx);
        const result = attempt(gainFor(count), roundCoins(price * count));

        executeSideEffects(ctx, result);

        if (notifyDone) notifyDone();

        return { result: { message: messageText(result, count) } };
      },
      cooldown,
      // Priced for what this click would actually take. A button the player cannot afford still
      // quotes one unit rather than zero coins, so the shelf price stays legible.
      tooltips: ctx => {
        const { price, count } = read(ctx);
        return [['coins', `${roundCoins(price * Math.max(1, count))}`]];
      }
    };
  };

// Retained for buyHorse, which is a one-off purchase rather than a quantity.
// type can be 'food', 'water', 'ammo', 'horse'
const adjustQuantityAndCost = (context, type) => {
  const { price, stock } = stateFor(context, type);

  return [stock, price];
};

export { adjustQuantityAndCost, affordable, attempt, buyAction, countFor, executeSideEffects, roundCoins, stateFor };
